// Filters that can be built up for use with the /scenarios/ endpoint
// (and other things).

import { ExpressionFilterSpecification } from "maplibre-gl";

import { flatten, removeNullUndefined } from "@nens/shared-utils";

// Immutable; methods that "change" the class instead return a new instance.
// This supports React, especially the idiom
//
//   const [filterSet, setFilterSet] = useState<FilterSet>(new FilterSet());
//   setFilterSet(filterSet => filterSet.add("status", "incomplete"))

type Value = string | number | boolean;
export type Filters = Record<string, Value[]>;

export class FilterSet {
  filters: Filters;

  constructor(other_filters: Filters = {}) {
    this.filters = { ...other_filters };
  }

  isEmpty() {
    return Object.keys(this.filters).length === 0;
  }

  equals(otherFilterSet: FilterSet) {
    const entries = Object.entries(this.filters);
    if (entries.length !== Object.keys(otherFilterSet.filters).length) return false;
    return entries.every(
      ([key, value]) =>
        key in otherFilterSet.filters &&
        JSON.stringify(value) === JSON.stringify(otherFilterSet.filters[key])
    );
  }

  set(key: string, value: Value) {
    // Use set() to set a filter that only has single values
    if (key in this.filters && this.filters[key].length === 1 && this.filters[key][0] === value) {
      return this;
    } else {
      return new FilterSet({
        ...this.filters,
        [key]: [value],
      });
    }
  }

  unset(key: string) {
    if (key in this.filters && this.filters[key].length > 0) {
      return new FilterSet({
        ...this.filters,
        [key]: [],
      });
    } else {
      return this;
    }
  }

  check(key: string, value: Value, checkboxValue: boolean) {
    // Use check for 'checkbox' filters that can have multiple values checked
    return checkboxValue ? this.add(key, value) : this.remove(key, value);
  }

  isChecked(key: string, value: Value) {
    return this.filters[key]?.includes(value) ?? false;
  }

  checkedValues(key: string) {
    return this.filters[key] ?? [];
  }

  add(key: string, value: Value) {
    const current = this.filters[key] ?? [];

    if (current.includes(value)) {
      return this;
    } else {
      return new FilterSet({ ...this.filters, [key]: [...current, value] });
    }
  }

  remove(key: string, value: Value) {
    const current = this.filters[key] ?? [];

    if (current.includes(value)) {
      return new FilterSet({ ...this.filters, [key]: current.filter((c) => c !== value) });
    } else {
      return this;
    }
  }

  removeAll(key: string) {
    if (this.filters[key] && this.filters[key].length > 0) {
      return new FilterSet({
        ...this.filters,
        [key]: [],
      });
    } else {
      return this;
    }
  }

  toUrlParams() {
    return removeNullUndefined(
      flatten(
        Object.keys(this.filters)
          .sort()
          .map((k) => {
            const values = this.filters[k];
            if (values.length === 0) return null;
            return values.map((v) => encodeURIComponent(k) + "=" + encodeURIComponent("" + v));
          })
      )
    ).join("&");
  }

  toMaplibreFilter(): ExpressionFilterSpecification {
    const filterPerKey = removeNullUndefined(
      Object.keys(this.filters).map((key) => this.filterForKey(key))
    );

    if (filterPerKey.length === 0) {
      return true;
    } else if (filterPerKey.length === 1) {
      return filterPerKey[0];
    } else {
      return ["all", ...filterPerKey];
    }
  }

  private filterForKey(key: string): ExpressionFilterSpecification | null {
    const values = this.filters[key] ?? [];
    if (values.length === 0) return null;

    if (key === "bbox") {
      // Irrelevant for the map, it already shows only its bbox by definition
      return null;
    }

    if (key === "status") {
      // In direct properties
      if (values.length === 1) {
        return ["==", ["get", key], values[0]];
      } else {
        return ["in", ["get", key], ["literal", values]];
      }
    }

    if (
      (key.endsWith("__ge") || key.endsWith("__le")) &&
      values.length === 1 &&
      typeof values[0] === "string" &&
      values[0].match(/\d\d\d\d-\d\d-\d\d/)
    ) {
      // Match timestamps.
      let timestampSeconds = new Date(values[0]).getTime() / 1000;
      if (key.endsWith("__le")) {
        // We want the whole last day inclusive, so add a day
        timestampSeconds += 60 * 60 * 24;
      }
      const comparison = key.endsWith("__ge") ? ">=" : "<=";
      return [
        comparison,
        ["to-number", ["get", "timestamp"]], // Name of the field is actually hardcoded.
        timestampSeconds,
      ];
    }

    // Assume other filters are in "meta".
    // We construct a bit of JSON and check with string contains
    const checks = removeNullUndefined(
      values.map(
        (value): ExpressionFilterSpecification =>
          typeof value === "number"
            ? // Numbers can be prefixes of other numbers, as there is no delimiter.
              // They are followed by either a "," or "}", check for that.
              [
                "any",
                ["in", `"${key}": ${value},`, ["get", "meta"]],
                ["in", `"${key}": ${value}}`, ["get", "meta"]],
              ]
            : // Make sure things like escaping are done as in JSON by using JSON.stringify
              ["in", `"${key}": ${JSON.stringify(value)}`, ["get", "meta"]]
      )
    );

    if (checks.length === 0) return null;
    if (checks.length === 1) return checks[0];
    return ["any", ...checks];
  }
}
